<template>
    <app-layout>
        <common-page-plain>
            <common-content-spacer height="medium">
                <common-page-header
                    :heading="$t('pages.governance.overview.title')"
                    :content="$t('pages.governance.overview.subtitle')"
                ></common-page-header>

                <div class="governance-filters">
                    <form-wrapper>
                        <form-row>
                            <form-cell width="half">
                                <select-field
                                    :label="$t('pages.governance.overview.filters.status')"
                                    :placeholder="$t('pages.governance.overview.filters.any_status')"
                                    :isNullable="true"
                                    :options="statusOptions"
                                    :formData="data"
                                    valuePath="status"
                                    @change="onFilterChange"
                                ></select-field>
                            </form-cell>
                            <form-cell width="half">
                                <date-range-field
                                    :label="$t('pages.governance.overview.filters.date_range')"
                                    :formData="data"
                                    openSide="left"
                                    valuePath="dateRange"
                                    @change="onFilterChange"
                                ></date-range-field>
                            </form-cell>
                        </form-row>
                    </form-wrapper>
                </div>

                <div class="governance-table">
                    <div class="governance-table__header">
                        <div>{{ $t('pages.governance.overview.table.referral') }}</div>
                        <div>{{ $t('pages.governance.overview.table.status') }}</div>
                        <div>{{ $t('pages.governance.overview.table.round') }}</div>
                        <div>{{ $t('pages.governance.overview.table.contacted') }}</div>
                        <div>{{ $t('pages.governance.overview.table.created') }}</div>
                        <div>{{ $t('pages.governance.overview.table.updated') }}</div>
                    </div>

                    <div v-if="isLoading" class="governance-table__state">
                        <common-loader :isProcessing="true"></common-loader>
                    </div>

                    <div v-else-if="hasError" class="governance-table__state governance-table__state--error">
                        {{ $t('pages.governance.overview.error') }}
                    </div>

                    <div v-else-if="!broadcasts.length" class="governance-table__state">
                        {{ $t('pages.governance.overview.empty') }}
                    </div>

                    <router-link
                        v-for="broadcast in broadcasts"
                        v-else
                        :key="broadcast.id"
                        :to="{ name: 'governance.detail', params: { referralId: broadcast.referralId } }"
                        class="governance-table__row"
                    >
                        <div>#{{ broadcast.referralId }}</div>
                        <div><governance-status-badge :status="broadcast.status"></governance-status-badge></div>
                        <div>{{ broadcast.currentRound }}</div>
                        <div>{{ broadcast.providersContactedCount }}</div>
                        <div>{{ formatDate(broadcast.createdAt) }}</div>
                        <div>{{ formatDate(broadcast.updatedAt) }}</div>
                    </router-link>
                </div>

                <div v-if="!isLoading && totalCount > limit" class="governance-pagination">
                    <common-button
                        :label="$t('common.pagination.previous')"
                        size="small"
                        type="secondary"
                        :isDisabled="offset === 0"
                        @click="goToPage(offset - limit)"
                    ></common-button>
                    <span class="governance-pagination__count">{{ paginationLabel }}</span>
                    <common-button
                        :label="$t('common.pagination.next')"
                        size="small"
                        type="secondary"
                        :isDisabled="offset + limit >= totalCount"
                        @click="goToPage(offset + limit)"
                    ></common-button>
                </div>
            </common-content-spacer>
        </common-page-plain>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import FormMixin from 'mixins/FormMixin'
    import GovernanceStatusBadge from 'components/page-elements/governance/GovernanceStatusBadge'
    import GovernanceAPI from 'api/GovernanceAPI'
    import BroadcastStatusEnum from 'enums/BroadcastStatusEnum'
    import dayjs from 'dayjs'

    export default {
        mixins: [PageWithTitleMixin, FormMixin],

        components: {
            AppLayout,
            GovernanceStatusBadge,
        },

        data() {
            return {
                isLoading: false,
                hasError: false,
                broadcasts: [],
                totalCount: 0,
                limit: 20,
                offset: 0,
            }
        },

        computed: {
            pageTitle() {
                return this.$t('pages.governance.overview.title')
            },

            statusOptions() {
                return Object.values(BroadcastStatusEnum).map((status) => ({ value: status, label: status }))
            },

            paginationLabel() {
                const from = this.totalCount ? this.offset + 1 : 0
                const to = Math.min(this.offset + this.limit, this.totalCount)
                return `${from}-${to} / ${this.totalCount}`
            },
        },

        methods: {
            onFilterChange(event) {
                this.change(event)
                this.offset = 0
                this.load()
            },

            goToPage(offset) {
                this.offset = offset
                this.load()
            },

            load() {
                this.isLoading = true
                this.hasError = false

                const { startDate, endDate } = this.data.dateRange || {}

                GovernanceAPI.getList({
                    status: this.data.status || undefined,
                    fromDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
                    toDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
                    limit: this.limit,
                    offset: this.offset,
                })
                    .then((response) => {
                        this.broadcasts = response
                        this.totalCount = parseInt(response._headers['x-total-count']) || response.length
                    })
                    .catch(() => {
                        this.hasError = true
                        this.broadcasts = []
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },

            formatDate(value) {
                return value ? dayjs(value).format('MMM D, YYYY h:mm A') : '—'
            },
        },

        created() {
            this.data.status = null
            this.data.dateRange = { startDate: null, endDate: null }
        },

        mounted() {
            this.load()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables';

    .governance-filters {
        margin-bottom: 24px;
        max-width: 640px;
    }

    .governance-table__header,
    .governance-table__row {
        display: grid;
        grid-template-columns: 0.8fr 1fr 0.6fr 0.8fr 1.2fr 1.2fr;
        gap: 1rem;
        align-items: center;
    }

    .governance-table__header {
        padding: 1rem;
        font-weight: bold;
        background: $color--bg-dropdown;
    }

    .governance-table__row {
        display: grid;
        padding: 0.75rem 1rem;
        border-bottom: 1px solid $color--border-base;
        color: inherit;
        text-decoration: none;

        &:hover {
            background: $color--bg-dropdown;
        }
    }

    .governance-table__state {
        padding: 2rem 1rem;
        text-align: center;
        color: $color--txt-base-3;

        &--error {
            color: $color--accent-8;
        }
    }

    .governance-pagination {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 16px;
    }

    .governance-pagination__count {
        font-size: 13px;
        color: $color--txt-base-3;
    }
</style>
